// tslint:disable: max-classes-per-file
import { BigNumber } from '@0x/utils'
import stakingUtils from 'app-lib/stakingUtils'
import errorUtils from 'bzx-common/src/lib/errorUtils'
import * as mobx from 'mobx'
import { FulcrumProvider } from '../../../../fulcrum/src/services/FulcrumProvider'
import RootStore from '../RootStore'
import StakingStore from './StakingStore'

interface IRewardsProps {
  ooki: BigNumber
  ookiVesting: BigNumber
  vestedOokiInRewards: BigNumber
  error: Error | null
  pendingSushiRewards: boolean
  pendingStakingRewards: boolean
  pendingVbzrxClaim: boolean
  sushi: BigNumber
  stableCoin: BigNumber
  stableCoinVesting: BigNumber
  vestedVbzrx: BigNumber
}

export default class Rewards implements IRewardsProps {
  /** OOKI received from staking */
  ooki = new BigNumber(0)
  /** OOKI pending to received from staked vbzrx */
  ookiVesting = new BigNumber(0)
  /**
   * The part of bzrx staking rewards that is not really staking rewards but instead
   * comes from the staked vbzrx that have vested.
   */
  vestedOokiInRewards = new BigNumber(0)
  error: Error | null = null
  pendingSushiRewards = false
  pendingStakingRewards = false
  pendingVbzrxClaim = false
  /** User earns Sushi when staking Sushi LP tokens (SLP) */
  sushi = new BigNumber(0)
  /**
   * "stablecoin" is actually the balance of 3CRV tokens that user can claim.
   * (It's called stablecoin in the contracts)
   */
  stableCoin = new BigNumber(0)
  stableCoinVesting = new BigNumber(0)
  provider: FulcrumProvider
  stakingStore: StakingStore
  rootStore: RootStore
  /** Balance of BZRX that the user can claim from his vested BZRX */
  vestedVbzrx = new BigNumber(0)

  get canClaimStakingRewards() {
    return this.ooki.gt(0.01) || this.stableCoin.gt(0.01)
  }

  get canClaimSushiRewards() {
    return this.sushi.gt(0.01)
  }

  get canClaimVestedBZRX() {
    return this.vestedVbzrx.gt(0.01)
  }

  /**
   * BZRX rewards include vbzrx staked that have vested + actual rewards.
   * This is the actual staking reward part.
   * Note: under certain circumstances, the vestedOokiInRewards may be bigger
   * than the total rewards. If that happens we prevent having a negative value
   * and set it to 0.
   */
  get actualBzrxStakingRewards() {
    const amount = this.ooki.minus(this.vestedOokiInRewards)
    if (amount.isNegative()) {
      return new BigNumber(0)
    }
    return amount
  }

  /**
   * Helper to set the value of one prop through a mobx action.
   */
  set(prop: keyof IRewardsProps, value: any) {
    ;(this[prop] as any) = value
  }

  /**
   * Helper to assign multiple props values through a mobx action.
   */
  assign(props: Partial<IRewardsProps>) {
    Object.assign(this, props)
  }

  async getAllRewards() {
    this.assign({ error: null, pendingStakingRewards: true })
    try {
      const fp = this.provider
      const rewards = await stakingUtils.getStakingRewards(fp)
      this.assign(rewards)
      const vestedOokiInRewards = await stakingUtils.getVestedOokiInRewards(
        fp,
        this.stakingStore.userBalances.staked.vbzrx
      )
      this.set('vestedOokiInRewards', vestedOokiInRewards)
      const vestedBzrx = await stakingUtils.getVestedBzrxBalance(fp)
      this.set('vestedVbzrx', vestedBzrx.div(10 ** 18))
      return rewards
    } catch (err) {
      const error = errorUtils.decorateError(err, {
        title: 'Failed to get rewards balances',
      })
      this.set('error', error)
    } finally {
      this.set('pendingStakingRewards', false)
    }
  }

  async claimStakingRewards(shouldRestake = false) {
    try {
      const { transactionStore } = this.rootStore
      this.assign({ error: null, pendingStakingRewards: true })
      const claimedAmounts = { ooki: this.ooki, stableCoin: this.stableCoin, sushi: this.sushi }
      const txSteps = stakingUtils.claimStakingRewards(this.provider, shouldRestake)
      const tx = await transactionStore.run('staking-claim', txSteps)
      if (tx.status === 'success') {
        this.assign({
          ooki: new BigNumber(0),
          stableCoin: new BigNumber(0),
          sushi: new BigNumber(0),
        })
        return claimedAmounts
      }
    } catch (err) {
      const error = errorUtils.decorateError(err, { title: 'Failed to claim rewards' })
      this.set('error', error)
      throw error
    } finally {
      this.set('pendingStakingRewards', false)
    }
  }

  /**
   * For users who hold vbzrx in their wallet and just want to claim their bzrx
   */
  async claimVestedBzrx() {
    try {
      const { transactionStore } = this.rootStore
      this.assign({ error: null, pendingVbzrxClaim: true })
      const txSteps = stakingUtils.claimVestedBZRX(this.provider)
      const bzrxAmount = this.vestedVbzrx
      const tx = await transactionStore.run('claim-vbzrx', txSteps)
      if (tx.status === 'success') {
        this.set('vestedVbzrx', new BigNumber(0))
        return bzrxAmount
      }
    } catch (err) {
      const error = errorUtils.decorateError(err, { title: 'Failed to claim vested BZRX' })
      this.set('error', error)
    } finally {
      this.set('pendingVbzrxClaim', false)
    }
  }

  async claimSushiRewards() {
    try {
      const { transactionStore } = this.rootStore
      this.assign({ error: null, pendingSushiRewards: true })
      const txSteps = stakingUtils.claimSushiRewards(this.provider)
      const sushiAmount = this.sushi
      const tx = await transactionStore.run('staking-claim', txSteps)
      if (tx.status === 'success') {
        this.set('sushi', new BigNumber(0))
        return sushiAmount
      }
    } catch (err) {
      const error = errorUtils.decorateError(err, { title: 'Failed to claim SUSHI' })
      this.set('error', error)
    } finally {
      this.assign({ pendingSushiRewards: false })
    }
  }

  clearBalances() {
    this.ooki = new BigNumber(0)
    this.ookiVesting = new BigNumber(0)
    this.stableCoin = new BigNumber(0)
    this.stableCoinVesting = new BigNumber(0)
    this.sushi = new BigNumber(0)
    this.vestedVbzrx = new BigNumber(0)
  }

  constructor(provider: FulcrumProvider, stakingStore: StakingStore) {
    this.provider = provider
    this.stakingStore = stakingStore
    this.rootStore = stakingStore.rootStore
    mobx.makeAutoObservable(this, undefined, { autoBind: true, deep: false })
  }
}
